import type { Node } from 'web-tree-sitter';
import type { LanguageId } from '../pipeline/types';
import { getParserFor } from './tree-sitter';

export interface MinifyOptions {
  keepDocstrings: boolean;
  /**
   * Also return where the comments that survived stripping ended up, in the
   * *output* string's coordinate space. B4 uses these as its compression
   * targets, so they have to line up with the text B1 actually emits, not with
   * the original source that was parsed.
   */
  reportKeptSpans?: boolean;
}

export interface KeptSpan {
  kind: 'docstring' | 'comment';
  start: number;
  end: number;
}

export interface MinifyResult {
  text: string;
  keptSpans: KeptSpan[];
}

/**
 * Strips comment nodes (never string/template literal contents — tree-sitter
 * distinguishes these node types, so this can't touch a literal by
 * construction) and blank lines. Deliberately does NOT re-indent code:
 * Python's indentation is semantically significant, and collapsing it
 * uniformly across languages risks corrupting meaning for a marginal token
 * saving — out of scope here.
 */
export async function stripCommentsAndBlankLines(
  content: string,
  languageId: LanguageId,
  options: MinifyOptions & { reportKeptSpans: true },
): Promise<MinifyResult>;
export async function stripCommentsAndBlankLines(
  content: string,
  languageId: LanguageId,
  options: MinifyOptions,
): Promise<string>;
export async function stripCommentsAndBlankLines(
  content: string,
  languageId: LanguageId,
  options: MinifyOptions,
): Promise<string | MinifyResult> {
  const parser = await getParserFor(languageId);
  const tree = parser.parse(content);
  if (!tree) return options.reportKeptSpans ? { text: content, keptSpans: [] } : content;

  const toRemove: Array<[number, number]> = [];
  const kept: KeptSpan[] = []; // original coordinates for now; remapped below
  const walk = (node: Node) => {
    if (node.type === 'comment') {
      const isJsDocStyle = languageId !== 'python' && node.text.startsWith('/**');
      if (options.keepDocstrings && isJsDocStyle) {
        kept.push({ kind: 'docstring', start: node.startIndex, end: node.endIndex });
      } else {
        toRemove.push([node.startIndex, node.endIndex]);
      }
      return;
    }
    for (const child of node.children) if (child) walk(child);
  };
  walk(tree.rootNode);

  const { text, originalToOutput } = rewrite(content, toRemove);
  if (!options.reportKeptSpans) return text;
  return { text, keptSpans: remapSpans(kept, originalToOutput) };
}

/**
 * Does the removal + trailing-whitespace + blank-line pass in one go, keeping a
 * per-character original->output index map so callers can follow a range
 * through the transformation. Character-at-a-time rather than
 * split/map/filter/join because there is no way to recover offsets afterwards.
 */
function rewrite(
  content: string,
  toRemove: Array<[number, number]>,
): { text: string; originalToOutput: Int32Array } {
  const removed = new Uint8Array(content.length);
  for (const [start, end] of toRemove) {
    for (let i = start; i < end && i < content.length; i++) removed[i] = 1;
  }

  // Surviving characters after comment removal, paired with where they came from.
  const chars: string[] = [];
  const origOf: number[] = [];
  for (let i = 0; i < content.length; i++) {
    if (removed[i]) continue;
    chars.push(content[i]);
    origOf.push(i);
  }

  const out: string[] = [];
  const originalToOutput = new Int32Array(content.length).fill(-1);
  let emittedAnyLine = false;

  for (let lineStart = 0; lineStart <= chars.length; ) {
    let lineEnd = lineStart;
    while (lineEnd < chars.length && chars[lineEnd] !== '\n') lineEnd++;

    // Trailing [ \t] trimmed, blank lines dropped entirely — same result the
    // old .map().filter().join('\n') produced.
    let trimmedEnd = lineEnd;
    while (trimmedEnd > lineStart && (chars[trimmedEnd - 1] === ' ' || chars[trimmedEnd - 1] === '\t')) trimmedEnd--;
    let blank = true;
    for (let i = lineStart; i < trimmedEnd; i++) {
      if (/\s/.test(chars[i])) continue;
      blank = false;
      break;
    }

    if (!blank) {
      if (emittedAnyLine) out.push('\n');
      emittedAnyLine = true;
      for (let i = lineStart; i < trimmedEnd; i++) {
        originalToOutput[origOf[i]] = out.length;
        out.push(chars[i]);
      }
    }

    if (lineEnd >= chars.length) break;
    lineStart = lineEnd + 1;
  }

  return { text: out.join(''), originalToOutput };
}

/**
 * Projects original-coordinate spans onto the output. A span whose characters
 * were all dropped (e.g. it was blank after trimming) is discarded rather than
 * emitted as a zero-width or wrong range — fail closed.
 */
function remapSpans(spans: KeptSpan[], originalToOutput: Int32Array): KeptSpan[] {
  const out: KeptSpan[] = [];
  for (const span of spans) {
    let start = -1;
    let end = -1;
    for (let i = span.start; i < span.end && i < originalToOutput.length; i++) {
      const mapped = originalToOutput[i];
      if (mapped < 0) continue;
      if (start < 0) start = mapped;
      end = mapped + 1;
    }
    if (start >= 0 && end > start) out.push({ kind: span.kind, start, end });
  }
  return out;
}
